import React, { useEffect, useState } from 'react';
import { X, Save, MessageCircle, Plus, Trash2, Tag, Clock, ArrowRight, Edit, Globe, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { STATUS_CONFIG, QUALIFICATION_CONFIG, SOURCE_CONFIG, formatDate, getSourceLabel } from '../utils/constants';
import WhatsAppModal from './WhatsAppModal';
import AiAnalysis from './AiAnalysis';
import { useAuth } from '../context/AuthContext';
import { hasFeature } from '../utils/features';
import CamposPropios, { leerCampos, valorDe, mostrar } from './CamposPropios';

export default function LeadDetail({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [waOpen, setWaOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const { tenant, user } = useAuth();
  const canWhatsApp = hasFeature(tenant, user, 'lead_whatsapp');
  const canAnalysis = hasFeature(tenant, user, 'ai_analysis') && hasFeature(tenant, user, 'inbox');
  const loadConvs = () => { if (canAnalysis) api.get(`/conversations/by-lead/${leadId}`).then(r => setConvs(r.data)).catch(() => {}); };
  useEffect(() => { loadConvs(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => { api.get(`/leads/${leadId}`).then(res => { setLead(res.data); setForm(res.data); }); };
  useEffect(() => { load(); }, [leadId]);

  /* Los campos que definió este cliente para sus leads. Se leen de su
     configuración, así el mismo CRM sirve para una organizadora de eventos y
     para cualquier otro negocio sin tocar la tabla. */
  const [campos, setCampos] = useState([]);
  const [propios, setPropios] = useState({});
  useEffect(() => { api.get('/settings').then(r => setCampos(leerCampos(r.data))).catch(() => {}); }, []);
  useEffect(() => {
    if (!lead) return;
    const v = {};
    campos.forEach(c => { v[c.key] = valorDe(lead, c.key); });
    setPropios(v);
  }, [lead, campos]);
  if (!lead) return null;

  const saveEdit = async () => {
    try { await api.put(`/leads/${leadId}`, { ...form, extra_data: propios }); toast.success('Lead actualizado'); setEditing(false); load(); }
    catch { toast.error('Error al guardar'); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try { await api.post(`/leads/${leadId}/notes`, { content: newNote }); setNewNote(''); load(); }
    catch { toast.error('Error'); }
  };

  const deleteNote = async (noteId) => { try { await api.delete(`/leads/${leadId}/notes/${noteId}`); load(); } catch { toast.error('Error'); } };
  const addTag = async () => {
    if (!newTag.trim()) return;
    try { await api.post(`/leads/${leadId}/tags`, { tag_name: newTag }); setNewTag(''); load(); }
    catch { toast.error('Error'); }
  };
  const removeTag = async (tagId) => { try { await api.delete(`/leads/${leadId}/tags/${tagId}`); load(); } catch { toast.error('Error'); } };
  const updateStatus = async (status) => { try { await api.put(`/leads/${leadId}/status`, { status }); load(); } catch { toast.error('Error'); } };
  const updateQual = async (q) => { try { await api.put(`/leads/${leadId}/qualification`, { qualification: q }); load(); } catch { toast.error('Error'); } };
  const deleteLead = async () => {
    if (!window.confirm(`¿Eliminar "${lead.business_name || 'este lead'}"?`)) return;
    try { await api.delete(`/leads/${leadId}`); toast.success('Lead eliminado'); onClose(); }
    catch { toast.error('Error'); }
  };

  const editFields = [
    { key: 'business_name', label: 'Nombre del negocio' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'email', label: 'Email' },
    { key: 'website', label: 'Sitio web' },
    { key: 'address', label: 'Dirección' },
    { key: 'city', label: 'Ciudad' },
    { key: 'category', label: 'Sector' },
    { key: 'service_interest', label: 'Servicio de interés' },
    { key: 'rating', label: 'Rating' },
    { key: 'inquiry', label: 'Consulta / Mensaje' },
    { key: 'utm_source', label: 'UTM Source' },
    { key: 'utm_medium', label: 'UTM Medium' },
    { key: 'utm_campaign', label: 'UTM Campaign' },
    { key: 'utm_content', label: 'UTM Content' },
    { key: 'utm_term', label: 'UTM Term' },
    { key: 'landing_page', label: 'Landing Page' },
    { key: 'referrer_url', label: 'Referrer' },
    { key: 'gclid', label: 'GCLID' },
    { key: 'fbclid', label: 'FBCLID' },
  ];

  const infoFields = [
    ['Teléfono', lead.phone], ['Email', lead.email], ['Web', lead.website],
    ['Dirección', lead.address], ['Ciudad', lead.city], ['Sector', lead.category],
    ['Rating', lead.rating], ['Base', lead.dataset_name]
  ];

  const trackingFields = [
    ['Fuente', getSourceLabel(lead)], ['UTM Source', lead.utm_source], ['UTM Medium', lead.utm_medium],
    ['Campaña', lead.utm_campaign], ['UTM Content', lead.utm_content],
    ['Landing Page', lead.landing_page], ['Referrer', lead.referrer_url],
    ['Servicio', lead.service_interest], ['Consulta', lead.inquiry],
    ['GCLID', lead.gclid], ['FBCLID', lead.fbclid]
  ];

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} />
      <div className="sidebar-panel">
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{lead.business_name || 'Lead'}</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost" onClick={deleteLead} title="Eliminar lead"><Trash2 size={16} color="var(--danger)" /></button>
            <button className="btn btn-ghost" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {/* Status & Qualification */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <select value={lead.status} onChange={e => updateStatus(e.target.value)}
              style={{ background: STATUS_CONFIG[lead.status]?.bg, color: STATUS_CONFIG[lead.status]?.color, border: 'none', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={lead.qualification} onChange={e => updateQual(e.target.value)}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
              {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <span className="badge" style={{ background: SOURCE_CONFIG[lead.source]?.bg || 'rgba(100,116,139,0.15)', color: SOURCE_CONFIG[lead.source]?.color || '#64748b' }}>
              {getSourceLabel(lead)}
            </span>
          </div>

          {/* Contact Info */}
          <div className="card" style={{ marginBottom: 16 }}>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {editFields.map(f => (
                  <div key={f.key} className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 11 }}>{f.label}</label>
                    <input value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} style={{ padding: '6px 10px', fontSize: 13 }} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveEdit}><Save size={14} /> Guardar</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Información</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} style={{ fontSize: 12, gap: 4 }}><Edit size={12} /> Editar</button>
                </div>
                {infoFields.map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, minWidth: 0 }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                    {label === 'Web' && val ? (
                      <a href={/^https?:\/\//i.test(val) ? val : 'https://' + val} target="_blank" rel="noopener noreferrer" title={val}
                        style={{ fontWeight: 500, color: 'var(--accent)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {String(val).replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}
                      </a>
                    ) : (
                      <span title={val || ''} style={{ fontWeight: 500, color: val ? 'var(--text-primary)' : 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{val || '—'}</span>
                    )}
                  </div>
                ))}
                {/* Los datos propios del negocio, junto al resto de la ficha */}
                {campos.map(c => (
                  <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, minWidth: 0 }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{c.label || c.key}</span>
                    <span style={{ fontWeight: 500, color: valorDe(lead, c.key) ? 'var(--text-primary)' : 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {mostrar(c, valorDe(lead, c.key))}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>Creado: {formatDate(lead.created_at)}</div>
              </div>
            )}
          </div>

          {/* En modo edición, los campos propios con su control según el tipo */}
          {editing && (
            <CamposPropios campos={campos} valores={propios}
              onCambio={(k, v) => setPropios(p => ({ ...p, [k]: v }))} />
          )}

          {/* Tracking Data */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Megaphone size={14} color="var(--accent)" />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Datos de tracking</span>
            </div>
            {trackingFields.map(([label, val]) => {
              const isUrl = typeof val === 'string' && /^https?:\/\//i.test(val);
              return (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                  {isUrl ? (
                    <a href={val} target="_blank" rel="noopener noreferrer" title={val} style={{ fontWeight: 500, color: 'var(--accent)', minWidth: 0, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{val.replace(/^https?:\/\/(www\.)?/i, '')}</a>
                  ) : (
                    <span style={{ fontWeight: 500, color: val ? 'var(--text-primary)' : 'var(--text-muted)', maxWidth: 260, wordBreak: 'break-all', textAlign: 'right' }}>{val || '—'}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* WhatsApp */}
          {canWhatsApp && lead.phone && (
            <button className="btn btn-sm" onClick={() => setWaOpen(true)}
              style={{ background: '#22c55e', color: '#fff', width: '100%', justifyContent: 'center', marginBottom: 16 }}>
              <MessageCircle size={16} /> Contactar por WhatsApp
            </button>
          )}

          {/* Tags */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Tags</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {lead.tags?.map(tag => (
                <span key={tag.id} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>
                  <Tag size={10} /> {tag.name}
                  <button onClick={() => removeTag(tag.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 0 }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Nuevo tag" style={{ padding: '4px 10px', fontSize: 12, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addTag()} />
              <button className="btn btn-secondary btn-sm" onClick={addTag}><Plus size={12} /></button>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Notas</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Agregar nota..." style={{ flex: 1, padding: '6px 10px', fontSize: 13 }} onKeyDown={e => e.key === 'Enter' && addNote()} />
              <button className="btn btn-primary btn-sm" onClick={addNote}><Plus size={14} /></button>
            </div>
            {lead.notes?.map(note => (
              <div key={note.id} style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(note.created_at)}</span>
                  <button onClick={() => deleteNote(note.id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={12} color="var(--text-muted)" /></button>
                </div>
                <p style={{ marginTop: 4 }}>{note.content}</p>
              </div>
            ))}
          </div>

          {/* Status History */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
              <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Historial de estados
            </div>
            {lead.history?.length > 0 ? lead.history.map(h => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                <span className="badge" style={{ background: STATUS_CONFIG[h.previous_status]?.bg, color: STATUS_CONFIG[h.previous_status]?.color, fontSize: 10 }}>{STATUS_CONFIG[h.previous_status]?.label || h.previous_status}</span>
                <ArrowRight size={12} color="var(--text-muted)" />
                <span className="badge" style={{ background: STATUS_CONFIG[h.new_status]?.bg, color: STATUS_CONFIG[h.new_status]?.color, fontSize: 10 }}>{STATUS_CONFIG[h.new_status]?.label}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 'auto', fontSize: 11 }}>{formatDate(h.changed_at)}</span>
              </div>
            )) : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sin cambios</p>}
          </div>

          {/* Análisis IA de la conversación */}
          {canAnalysis && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Conclusión del agente IA</div>
              {convs.length > 0
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{convs.map(c => (
                    <div key={c.id}>
                      {convs.length > 1 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{c.channel === 'instagram' ? 'Instagram' : 'WhatsApp'}</div>}
                      <AiAnalysis conv={c} onUpdated={loadConvs} />
                    </div>
                  ))}</div>
                : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Este lead no tiene conversaciones con el agente.</p>}
            </div>
          )}

          {/* Contact Log */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
              <MessageCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Historial de contactos
            </div>
            {lead.contacts?.length > 0 ? lead.contacts.map(c => (
              <div key={c.id} style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>WhatsApp</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(c.contacted_at)}</span>
                </div>
                {c.template_name && <div style={{ color: 'var(--text-muted)' }}>Plantilla: {c.template_name}</div>}
              </div>
            )) : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sin contactos</p>}
          </div>
        </div>
      </div>
      {waOpen && <WhatsAppModal lead={lead} onClose={() => { setWaOpen(false); load(); }} />}
    </>
  );
}
